import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Stack, Typography } from '@mui/material';
import { listHistory } from 'shared/api/user_profile';
import { useAuth, useRequest } from 'shared/hooks';
import { InsetNavLink } from 'shared/components/NavLink';

// Create a key for each module that has saved history.
const moduleNames = [
  'Building',
  'Coupon',
  'Tenant',
  'Ticket',
  'User',
  'Voucher',
];

// Mounts twice (withSideNav keeps the mobile drawer mounted beside the desktop one), so all
// instances share one in-flight fetch per user.
const inFlightByUser = new Map();

// Displays a grid of a users favorite history items.
export default function FavoriteItems() {
  const [state, setState] = useState({});
  const { user: { id: userId } } = useAuth();
  const { request } = useRequest();

  useEffect(() => {
    let active = true;
    (async () => {
      if (!userId) return;

      // Reuse a pending fetch for this user if another instance already started one.
      let pending = inFlightByUser.get(userId);
      if (!pending) {
        pending = Promise
          .all(moduleNames.map((module) => request(listHistory(userId, module))))
          .finally(() => inFlightByUser.delete(userId));
        inFlightByUser.set(userId, pending);
      }
      const historyResult = await pending;
      if (!active) return;

      const newState = {};

      moduleNames.forEach((module, i) => {
        if (historyResult[i] && historyResult[i].favorites.length > 0) {
          newState[module] = historyResult[i].favorites;
        }
      });
      if (Object.keys(newState).length > 0) {
        setState(newState);
      }
    })();
    return () => {
      active = false;
    };
  }, [userId, request]);

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography variant="h6" className="side-nav-title">
          Favorites
        </Typography>
      </Grid>
      <Grid
        container
        spacing={2}
        sx={{
          overflow: 'auto',
          maxHeight: '82dvh',
          // Room for the links' focus rings inside the scroll box.
          p: '4px',
          m: '-4px',
        }}
      >
        {Object.keys(state)
          .map((moduleName) => (
            <Grid key={`${moduleName}-favorites`} size={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '1.15rem',
                }}
              >
                {`${moduleName}s`}
              </Typography>
              <Stack>
                {state[moduleName].map((item, index) => (
                  <Grid size={12} key={`${moduleName}-favorite-${index + 1}`}>
                    <InsetNavLink favorited text={item[1]} path={item[0]} />
                  </Grid>
                ))}
              </Stack>
            </Grid>
          ))}
      </Grid>
    </Grid>
  );
}
